import { ReactElement } from 'react'
import { ProductLocationModel } from 'interfaces'
import styled from 'styled-components'
import { defaultTheme } from '../../../theme'
import {
  Warning,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Store as StoreIcon,
} from '@mui/icons-material'
import { I18n } from 'common'
import { LocationType } from '../../../helpers/locationType'
import { translateApiScheduleText } from '../../../helpers/utils'
import WarningLabel from '../../WarningLabel'

interface Props {
  disableStore: boolean
  isQtyAvailable: boolean
  selected: boolean
  location: ProductLocationModel
}

export const LocationInfo = (props: Props): ReactElement => {
  const { disableStore, isQtyAvailable, selected, location } = props

  return (
    <LocationInfoWrapper>
      <StoreName title={location.called}>{location.called}</StoreName>
      {!disableStore && (
        <div>
          <StoreInfoBoldText>
            {isQtyAvailable ? <StyledCheckCircleIcon /> : <StyledBlockIcon />}
            {location.qtyAvailable} <I18n name="available" />
          </StoreInfoBoldText>
          <StoreScheduleText title={location.schedule}>
            {translateApiScheduleText(location.schedule)}
          </StoreScheduleText>
        </div>
      )}
      {disableStore && !selected && (
        <div>
          <WarningWrapper>
            {isQtyAvailable && <WarningIcon />}
            {!isQtyAvailable && <WarningNoQuantity />}
            <WarningContent>
              <StoreInfoBoldText>
                {location.qtyAvailable} <I18n name="available" />
              </StoreInfoBoldText>
              {isQtyAvailable && (
                <LocationWarning>
                  <I18n name="selectAnotherLocation" />
                </LocationWarning>
              )}
            </WarningContent>
          </WarningWrapper>
          <StoreScheduleText title={location.schedule}>
            {translateApiScheduleText(location.schedule)}
          </StoreScheduleText>
        </div>
      )}
      {disableStore && selected && (
        <div>
          <StoreInfoBoldText>
            {isQtyAvailable && <StyledCheckCircleIcon />}
            {location.qtyAvailable} <I18n name="available" />
          </StoreInfoBoldText>
          <StoreScheduleText title={location.schedule}>
            {translateApiScheduleText(location.schedule)}
          </StoreScheduleText>
        </div>
      )}
      {location.order && (
        <FlexDiv>
          <StoreInfoBoldText>
            <I18n name="order" />:
          </StoreInfoBoldText>
          <EllipsisText title={location.order}>
            {translateApiScheduleText(location.order)}
          </EllipsisText>
        </FlexDiv>
      )}
      {location.estimatedDelivery && (
        <FlexDiv>
          <StoreInfoBoldText>
            <I18n name="estimatedDelivery" />:
          </StoreInfoBoldText>
          <EllipsisText title={location.estimatedDelivery}>
            {translateApiScheduleText(location.estimatedDelivery)}
          </EllipsisText>
        </FlexDiv>
      )}
      {location?.locType === LocationType.ORDER_FROM_PRIMARY && (
        <div>
          <WarningLabel name="OrdersWillBeProcessedByYourLocalStore" />
        </div>
      )}
    </LocationInfoWrapper>
  )
}

export const MyStoreTag = (): ReactElement => (
  <FlexDiv>
    <StyledStoreIcon />
    <SpanLabel>
      <I18n name="myStore" />
    </SpanLabel>
  </FlexDiv>
)

export default LocationInfo

const ellipsisText = `
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`

const LocationInfoWrapper = styled.div`
  min-width: 0;
  max-width: 100%;
`

const StoreName = styled.div`
  font-family: Oswald, serif;
  font-style: normal;
  font-weight: bold;
  font-size: 20px;
  line-height: 30px;
  color: #050505;
  ${ellipsisText}
`

const StoreInfoBoldText = styled.div`
  font-family: Roboto, serif;
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
  color: #050505;
  padding-right: 5px;
`

const StoreScheduleText = styled.div`
  font-family: Roboto, serif;
  font-style: normal;
  font-size: 15px;
  line-height: 30px;
  color: ${defaultTheme.colors.almostBlack};
  ${ellipsisText}
`

const StyledBlockIcon = styled(BlockIcon)`
  color: #d9001b;
  margin-top: -5px;
  padding-right: 5px;
`

const StyledCheckCircleIcon = styled(CheckCircleIcon)`
  color: #7ebd34;
  margin-top: -5px;
  padding-right: 5px;
`

const WarningWrapper = styled.div`
  display: flex;
  min-width: 0;
  max-width: 100%;
`

const WarningContent = styled.div`
  min-width: 0;
  flex: 1;
`

const LocationWarning = styled.p`
  font-family: 'Roboto';
  font-style: normal;
  font-size: 18px;
  line-height: 24px;
  color: ${(p) => p.theme.colors.text};
  margin: 0;
  min-width: 0;
  ${ellipsisText}
`
const WarningIcon = styled(Warning)`
  color: ${(p) => p.theme.colors.primary};
  width: 22px;
  height: 18.21px;
  margin-top: 1px;
  margin-right: 3px;
`

const WarningNoQuantity = styled(StyledBlockIcon)`
  padding: 0;
  margin: 0;
  margin-right: 3px;
`

const FlexDiv = styled.div`
  display: flex;
  margin-top: 4px;
  margin-bottom: 4px;
  min-width: 0;
  max-width: 100%;
`

const EllipsisText = styled.span`
  min-width: 0;
  ${ellipsisText}
`

const SpanLabel = styled.div`
  padding-left: 0.3rem;
  font-family: Roboto, serif;
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
  color: #050505;
`

const StyledStoreIcon = styled(StoreIcon)`
  color: #7ebd34;
`
